import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import * as R from 'ramda';

import { requestDownloadFiles } from '@atom/actions/fileActions';
import * as inventoryAssetActions from '@atom/actions/inventoryAssetActions';
import PaginationControl from '@atom/components/common/PaginationControl';
import CarouselWrapper from '@atom/components/common/photoCarousel/CarouselWrapper';
import textConstants from '@atom/constants/textConstants';
import {
  GET_MEDIA,
  MEDIA_BULK_DELETE,
  MEDIA_DELETE,
  MEDIA_UPDATE,
} from '@atom/graph/media';
import { useFileUpload } from '@atom/hooks/useFileUpload';
import { useMediaSearch } from '@atom/hooks/useMediaSearch';
import { Snackbar } from '@atom/mui';
import { InventoryAssetDetailType } from '@atom/types/inventory';
import {
  MediaBulkDeleteInput,
  MediaItem,
  MediaType,
  MediaUpdateInput,
  SubjectType,
} from '@atom/types/media';
import { toggleFromSet } from '@atom/utilities/setUtilities';

import PhotosHeader from './PhotosHeader';
import PhotoTile from './PhotoTile';

import classes from './inventoryPhotos.module.css';

const INITIAL_LIMIT = 25;
const CHARACTER_MIN = 3;

interface Props {
  inventoryAssetDetail: InventoryAssetDetailType;
  canCreateMedia: boolean;
  canMarkAsCoverPhoto: boolean;
  canUpdateMedia: boolean;
  canDeleteMedia: boolean;
}

const InventoryPhotos = ({
  inventoryAssetDetail,
  canCreateMedia,
  canMarkAsCoverPhoto,
  canUpdateMedia,
  canDeleteMedia,
}: Props) => {
  const dispatch = useDispatch();

  const [media, setMedia] = useState<MediaItem[]>([]);
  const [mediaTotal, setMediaTotal] = useState<number>(0);
  const [searchValue, setSearchValue] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(INITIAL_LIMIT);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set([]));

  const [uploadFiles] = useFileUpload();

  const onCompleted = data => {
    setMedia(data?.media?.media);
    setMediaTotal(data?.media?.totalCount);
  };

  const { refetch, loading: loadingMedia } = useQuery(GET_MEDIA, {
    variables: {
      input: {
        parentSubjectIds: [inventoryAssetDetail?.id],
        subjectTypes: [SubjectType.INVENTORY_ASSET],
        type: MediaType.IMAGE,
        page,
        limit,
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted,
  });

  const [mediaSearch, { loadingSearch }] = useMediaSearch({
    query: searchValue,
    parentSubjectIds: [inventoryAssetDetail?.id],
    subjectTypes: [SubjectType.INVENTORY_ASSET],
    type: MediaType.IMAGE,
    limit,
    characterMin: CHARACTER_MIN,
  });

  useEffect(() => {
    if (searchValue.length >= CHARACTER_MIN) {
      setMedia(mediaSearch.media);
      setMediaTotal(mediaSearch.totalCount);
    }
  }, [mediaSearch.media, mediaSearch.totalCount, searchValue]);

  const [mediaUpdate] = useMutation<
    { mediaUpdate: MediaItem },
    { input: MediaUpdateInput }
  >(MEDIA_UPDATE);

  const [mediaDelete, { loading: loadingDelete }] = useMutation<
    { mediaDelete: boolean },
    { id: string }
  >(MEDIA_DELETE);

  const [mediaBulkDelete] = useMutation<
    { mediaBulkDelete: boolean },
    { input: MediaBulkDeleteInput }
  >(MEDIA_BULK_DELETE);

  const resetToFirstPage = () => {
    if (page === 1) {
      refetch({
        input: {
          parentSubjectIds: [inventoryAssetDetail?.id],
          subjectTypes: [SubjectType.INVENTORY_ASSET],
          type: MediaType.IMAGE,
          page: 1,
          limit,
        },
      });
    } else {
      setPage(1);
    }
  };

  const handleUpload = async event => {
    const { files } = event.target;

    if (!files.length) {
      return;
    }

    await uploadFiles({
      files,
      subjectId: inventoryAssetDetail?.id,
      subjectType: SubjectType.INVENTORY_ASSET,
      parentSubjectId: inventoryAssetDetail?.id,
    });

    refetch();
  };

  const deleteMedia = async (id: string) => {
    try {
      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_DELETING_TEXT,
      });

      await mediaDelete({
        variables: {
          id,
        },
      });

      Snackbar.info({
        message: 'Successfully deleted file',
      });

      setSelectedItems(new Set([]));
      resetToFirstPage();
    } catch (err) {
      Snackbar.error({
        message: 'Failed to delete file',
      });
    }
  };

  const renameMedia = async (id: string, subjectId: string, name: string) => {
    const res = await mediaUpdate({
      variables: {
        input: {
          id,
          name,
        },
      },
    });

    const updatedMedia = media.map((mediaItem: MediaItem) => {
      return mediaItem.id === id ? res?.data?.mediaUpdate : mediaItem;
    });

    setMedia(updatedMedia);
  };

  const incrementPage = (increment: number) => {
    setPage(page + increment);
    setSelectedItems(new Set([]));
  };

  const updateLimit = (newLimit: string) => {
    setLimit(Number(newLimit));
    setSelectedItems(new Set([]));
    resetToFirstPage();
  };

  const onChange = (value: string) => {
    setSearchValue(value);
  };

  const clearSearch = async () => {
    setSearchValue('');
    setSelectedItems(new Set([]));
    resetToFirstPage();
  };

  const toggleCheckbox = (id: string) => {
    setSelectedItems(toggleFromSet(selectedItems, id));
  };

  const downloadMultipleFiles = () => {
    const selectedMedia = media.filter(medium =>
      [...selectedItems].includes(medium.id),
    );

    dispatch(requestDownloadFiles(selectedMedia));
  };

  const deleteMultipleFiles = async () => {
    await mediaBulkDelete({
      variables: {
        input: {
          ids: [...selectedItems],
        },
      },
    });

    setSelectedItems(new Set([]));
    resetToFirstPage();
  };

  const selectAll = () => {
    const mediaIds = media.map(medium => medium.id);
    setSelectedItems(new Set(mediaIds));
  };

  const setAsPrimaryMedia = (mediaId: string) => {
    const selectedMedium = media.find(medium => medium.id === mediaId);

    const newMainPhotoFileId =
      inventoryAssetDetail?.mainPhotoFileId === selectedMedium.fileId
        ? ''
        : selectedMedium.fileId;

    dispatch(
      inventoryAssetActions.requestInventoryAssetDetailUpdate({
        id: inventoryAssetDetail?.id,
        rootAssetId: inventoryAssetDetail?.id,
        mainPhotoFileId: newMainPhotoFileId,
      }),
    );
  };

  return (
    <div className={classes.bodyContainer}>
      <div className={classes.leftBodyPaneFixed} />
      <div className={classes.rightBodyPaneFill}>
        <PhotosHeader
          media={media}
          selectLimit={R.length(media)}
          searchValue={searchValue}
          clearSearch={clearSearch}
          onChange={event => onChange(event.target.value)}
          uploadFiles={handleUpload}
          downloadMultipleFiles={downloadMultipleFiles}
          deleteMultipleFiles={deleteMultipleFiles}
          selectAll={selectAll}
          deselectAll={() => setSelectedItems(new Set([]))}
          selectedItems={[...selectedItems]}
          loading={loadingSearch || loadingMedia}
          canCreateMedia={canCreateMedia}
          canDeleteMedia={canDeleteMedia}
        />
        <div className={classes.photoBlock}>
          <div className={classes.photoTilesContainer}>
            {media.map((medium: MediaItem) => {
              const isChecked = selectedItems.has(medium.id);

              return (
                <CarouselWrapper
                  key={medium.id}
                  media={media}
                  renameMedia={renameMedia}
                  selectedMediaId={medium.id}
                  removeFile={deleteMedia}
                  subjectId={medium.subjectId}
                  toggleCheckbox={toggleCheckbox}
                  isChecked={isChecked}
                  setAsPrimaryMedia={setAsPrimaryMedia}
                  primaryMediaId={inventoryAssetDetail?.mainPhotoFileId}
                  canMarkAsCoverPhoto={canMarkAsCoverPhoto}
                  canUpdateMedia={canUpdateMedia}
                  canDeleteMedia={canDeleteMedia}
                  refetchMedia={refetch}
                >
                  <PhotoTile
                    media={medium}
                    renameMedia={renameMedia}
                    deleteMedia={deleteMedia}
                    loadingDelete={loadingDelete}
                    canMarkAsCoverPhoto={canMarkAsCoverPhoto}
                    canUpdateMedia={canUpdateMedia}
                    canDeleteMedia={canDeleteMedia}
                    mainMediaId={inventoryAssetDetail?.mainPhotoFileId}
                    setAsPrimaryMedia={setAsPrimaryMedia}
                  />
                </CarouselWrapper>
              );
            })}
          </div>
        </div>
        {!!media.length && (
          <div className={classes.paginationContainer}>
            <PaginationControl
              incrementPage={incrementPage}
              updateLimit={updateLimit}
              page={page}
              limit={limit}
              total={mediaTotal}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryPhotos;
